<template>
  <component
    :is="isLink ? 'a' : 'button'"
    v-bind="elementAttrs"
    :class="rootClass"
    @click="handleClick"
  >
    <!-- Spinner overlay is always present; sf-button.scss shows it (and hides
         the rest) only while aria-busy, so the button keeps its width when
         loading. -->
    <span class="sf-button__spinner">
      <i class="fa fa-spinner fa-spin" aria-hidden="true" />
    </span>
    <i
      v-if="iconBefore"
      class="fa"
      :class="`fa-${iconBefore}`"
      aria-hidden="true"
    />
    <span v-if="!iconOnly" class="sf-button__label"><slot /></span>
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs, watchEffect } from 'vue'
import { SfLog } from '@app/adapters/logger'
import env from '@app/utils/environment'

// Light-DOM design-system button — the single Vue implementation, used wherever
// the application stylesheet is loaded (e.g. the report graph editor). Its BEM
// classes and Font Awesome <i> markup match the Rails Buttons::SfButtonComponent
// exactly; the parity system spec pins the two together.

defineOptions({ name: 'SfButtonDs' })

// Mirrors Buttons::SfButtonComponent VARIANTS/SIZES (the Rails source of truth).
type Variant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'ai'
type Size = 'sm' | 'md' | 'lg'

interface Props {
  variant?: Variant
  size?: Size
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  iconOnly?: boolean
  iconBefore?: string
  href?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  size: 'lg',
  type: 'button',
  disabled: false,
  loading: false,
  iconOnly: false,
  iconBefore: undefined,
  href: undefined,
})

const isLink = computed(() => Boolean(props.href))
const inactive = computed(() => props.disabled || props.loading)

// Dev-only mirror of the Rails component's icon_only accessible-name guard: an
// icon-only button has no visible label, so it needs a name from aria-label,
// aria-labelledby, or title (all arrive as fall-through attrs, not props). The
// Rails component adds an `aria_label:` shortcut because its equivalent is
// nested (html: { aria: { label: } }); here `:aria-label="…"` is already the
// idiomatic form, so no matching prop is needed.
if (env.__DEV__) {
  const attrs = useAttrs()
  watchEffect(() => {
    if (
      props.iconOnly &&
      !attrs['aria-label'] &&
      !attrs['aria-labelledby'] &&
      !attrs.title
    ) {
      SfLog.warn(
        '[SfButtonDs] icon-only button needs an accessible name: pass aria-label, aria-labelledby, or title'
      )
    }
  })
}

// <a> and <button> take different attributes, so build the right set per tag
// instead of scattering isLink checks across the template. A disabled link
// keeps its href (stays focusable/announced) and uses aria-disabled; a disabled
// button uses the native disabled attribute.
const elementAttrs = computed(() =>
  isLink.value
    ? {
        href: props.href,
        'aria-disabled': inactive.value ? 'true' : undefined,
        'aria-busy': props.loading ? 'true' : undefined,
      }
    : {
        type: props.type,
        disabled: inactive.value,
        'aria-busy': props.loading ? 'true' : undefined,
      }
)

const rootClass = computed(() => [
  'sf-button',
  `sf-button--${props.variant}`,
  `sf-button--${props.size}`,
  { 'sf-button--icon-only': props.iconOnly },
])

// A disabled <a> still navigates on Enter even with pointer-events: none, so
// block activation here; the native disabled attribute already covers <button>.
function handleClick(event: Event): void {
  if (isLink.value && inactive.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../scss/components/sf-button';
</style>
